'use client';

import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CmsImage from '@/components/ui/CmsImage';
import type { TextImageComponent } from '@/types/product';
import { localizeUrl, pickButton, stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';

type ProductsCostPlanBannerProps = {
  locale: string;
  textImageComponent?: TextImageComponent;
};

export default function ProductsCostPlanBanner({
  locale,
  textImageComponent,
}: ProductsCostPlanBannerProps) {
  if (!textImageComponent) return null;

  const title = textImageComponent.title?.trim();
  const body = stripHtml(textImageComponent.subHeading);
  const media = textImageComponent.media;
  const bgImage = getStrapiOriginalMediaUrl(media);
  const bgImageAlt = media?.alternativeText?.trim() || title || '';

  const cta = pickButton(textImageComponent.buttonDark as never, 'Solid');
  const buttonLabel = cta?.buttonText?.trim();
  const buttonHref = localizeUrl(cta?.buttonUrl, locale);

  if (!title && !body && !bgImage) return null;

  return (
    <section className="bg-black max-sm:!px-4" aria-labelledby="products-cost-plan-heading">
      <div className="container mx-auto">
        <div className="relative min-h-[380px] overflow-hidden rounded-2xl shadow-sm md:min-h-[440px] lg:min-h-[480px]">
          {bgImage ? (
            <CmsImage
              src={bgImage}
              alt={bgImageAlt}
              fill
              quality={90}
              className="object-cover object-right"
              sizes="(min-width: 1280px) 1200px, 100vw"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
          ) : null}
          {/* Light wash on the left so black type stays readable on the desk photo */}
          <div
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.94)_0%,rgba(255,255,255,0.78)_28%,rgba(255,255,255,0.35)_52%,transparent_78%)]"
            aria-hidden
          />

          <div className="relative z-10 flex min-h-[380px] max-w-[min(100%,520px)] flex-col justify-center px-8 py-10 md:min-h-[440px] md:px-12 md:py-12 lg:min-h-[480px] lg:max-w-[560px] lg:px-14 lg:py-14">
            <h2
              id="products-cost-plan-heading"
              className="text-3xl font-bold leading-[1.15] tracking-tight text-[#0A0A0A] md:text-4xl lg:text-[2.125rem] lg:leading-[1.2]"
            >
              {title}
            </h2>
            <p className="mt-4 max-w-md text-sm font-normal leading-relaxed text-[#0A0A0A]/90 md:text-base">
              {body}
            </p>
            <div className="mt-8">
              {buttonLabel && buttonHref ? (
                <SwapTextButton
                  text={buttonLabel}
                  href={buttonHref}
                  {...getCmsButtonLinkProps(cta)}
                  showArrow={false}
                  className="rounded-md !bg-[#E82127] !text-white shadow-md hover:!bg-[#cf1e22] hover:!text-white"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
